import { motion } from "framer-motion"
import { formatDate } from "lopermut"
import { RadarIcon } from "lucide-react"
import { ReactNode, useEffect, useMemo, useRef } from "react"
import { Bar, BarChart, CartesianGrid, TooltipContentProps, XAxis, YAxis } from "recharts"
import { CardContent } from "../../molecules/Card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../molecules/Chart"
import { cn } from "../../utils"

const ACTIVITY_BAR_SIZE = 20

type Props = {
  data?: {
    date?: string | null
    baseValue: number
  }[]
  chartConfig: ChartConfig
  isPending: boolean
  noDataT?: {
    title: string
    description: string
  }
  action?: ReactNode
  chartClassName?: string
}

function getColor(chartConfig: ChartConfig) {
  return chartConfig.baseValue?.color || "var(--chart-primary)"
}

export const SimpleActivityChart = ({
  data,
  chartConfig,
  isPending,
  noDataT,
  action,
  chartClassName,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const noData = !isPending && !data?.length

  const chartData = useMemo(() => {
    return data?.map((item) => {
      const formattedDate = item.date ? formatDate(item.date, "MMM d") : null

      if (noData) {
        const baseValue = Math.ceil(Math.random() * 80)

        return {
          date: formattedDate,
          baseValue,
        }
      }

      return {
        date: formattedDate,
        baseValue: item.baseValue,
      }
    })
  }, [data, noData])

  const maxValue = useMemo(() => {
    return Math.round(Math.max(...(chartData?.map((item) => item.baseValue * 1.1) ?? [])))
  }, [chartData])

  useEffect(() => {
    if (containerRef.current && chartData?.length) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth
    }
  }, [chartData])

  return (
    <div className="flex flex-col w-full relative rounded-none border-none gap-2">
      <div
        className={cn(
          "flex flex-row items-end justify-between gap-4 pr-4",
          !action && "justify-end",
        )}
      >
        {action}
        <ActivityTooltipInfo
          className={cn(noData && "pointer-events-none grayscale opacity-15")}
          chartConfig={chartConfig}
        />
      </div>
      {noData && (
        <div className="flex items-center justify-center absolute inset-0 z-10">
          <div className="flex flex-col gap-2 py-14 items-center justify-center text-center m-auto max-w-sm">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <RadarIcon className="size-10 text-muted-foreground mb-3" strokeWidth={1.5} />
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <h2 className="text-md text-foreground text-stroke">{noDataT?.title}</h2>
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <p className="text-sm text-muted-foreground text-stroke-sm">{noDataT?.description}</p>
            </motion.div>
          </div>
        </div>
      )}
      <CardContent
        className={cn(
          "flex flex-col p-0 gap-2 items-end",
          noData && "pointer-events-none grayscale opacity-15",
        )}
      >
        <div ref={containerRef} className="w-full overflow-x-auto pb-2 -mb-2">
          <ChartContainer
            config={chartConfig}
            className={cn("aspect-auto h-[180px]", chartClassName)}
            style={{
              width: `${(chartData?.length ?? 0) * ACTIVITY_BAR_SIZE}px`,
              minWidth: "100%",
            }}
          >
            <BarChart accessibilityLayer data={chartData} margin={{ top: 5, bottom: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={30}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                width={30}
                domain={[0, maxValue]}
                allowDataOverflow
                hide
              />
              <ChartTooltip
                content={(props: TooltipContentProps) => {
                  return <ChartTooltipContent {...props} />
                }}
              />
              <Bar
                dataKey="baseValue"
                fill={getColor(chartConfig)}
                barSize={ACTIVITY_BAR_SIZE}
                className="transition-all"
              />
            </BarChart>
          </ChartContainer>
        </div>
      </CardContent>
    </div>
  )
}

const ActivityTooltipInfo = ({
  className,
  chartConfig,
}: {
  className?: string
  chartConfig: ChartConfig
}) => {
  return (
    <div className={cn("flex items-center justify-end gap-4 flex-wrap", className)}>
      {Object.entries(chartConfig).map(([key, config]) => {
        return (
          <div key={key} className="flex items-center gap-1.5">
            <div
              className="size-2 rounded-full opacity-100"
              style={{ background: getColor(chartConfig) }}
            />
            <span className="text-sm text-muted-foreground">{config.label}</span>
          </div>
        )
      })}
    </div>
  )
}
